/**
 * PR-24 — User Popularity Engine + Leaderboards + Badges + Referral.
 * Frontend-only store backed by localStorage. SuperAdmin AI and Partner
 * Portal can read aggregated rankings via the exported selectors. All
 * mutations emit audit events through trackEvent.
 */
import { trackEvent } from "@/lib/audit";

export type LeaderboardScope =
  | "global"
  | "country"
  | "city"
  | "team"
  | "match"
  | "sponsor"
  | "friends";

export type LeaderboardCategory =
  | "top_fans"
  | "top_analysts"
  | "top_predictors"
  | "top_influencers"
  | "top_referrers"
  | "top_consumers"
  | "top_winners";

export type UserTitle =
  | "rookie_fan"
  | "tactical_analyst"
  | "oracle_master"
  | "mvp_predictor"
  | "community_star"
  | "sponsor_champion"
  | "world_cup_legend";

export type UserBadge =
  | "verified_fan"
  | "top_10"
  | "streak_30"
  | "prediction_expert"
  | "brand_ambassador";

export interface PopularitySignals {
  messages_sent: number;
  likes_received: number;
  surveys_answered: number;
  predictions_correct: number;
  active_minutes: number;
  shares: number;
  referrals: number;
  redemptions: number;
  daily_streak: number;
}

export interface UserPopularityRecord {
  user_id: string;
  handle: string;
  city?: string;
  country?: string;
  team?: string;
  signals: PopularitySignals;
  popularity_score: number;
  community_rank: number;
  global_rank: number;
  city_rank: number;
  country_rank: number;
  titles: UserTitle[];
  badges: UserBadge[];
  referral_code: string;
  updated_at: number;
}

const KEY = "vs:pr24:popularity";

const WEIGHTS: Record<keyof PopularitySignals, number> = {
  messages_sent: 1,
  likes_received: 2,
  surveys_answered: 3,
  predictions_correct: 5,
  active_minutes: 0.2,
  shares: 4,
  referrals: 10,
  redemptions: 6,
  daily_streak: 2,
};

function load(): UserPopularityRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as UserPopularityRecord[]) : [];
  } catch {
    return [];
  }
}

function save(rows: UserPopularityRecord[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(rows));
}

function computeScore(s: PopularitySignals): number {
  return Object.keys(WEIGHTS).reduce(
    (acc, k) => acc + (s[k as keyof PopularitySignals] || 0) * WEIGHTS[k as keyof PopularitySignals],
    0,
  );
}

function genCode(): string {
  const alpha = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let s = "VS";
  for (let i = 0; i < 6; i++) s += alpha[Math.floor(Math.random() * alpha.length)];
  return s;
}

function rerank(rows: UserPopularityRecord[]): UserPopularityRecord[] {
  const sorted = [...rows].sort((a, b) => b.popularity_score - a.popularity_score);
  const byCountry = new Map<string, number>();
  const byCity = new Map<string, number>();
  sorted.forEach((r, i) => {
    r.global_rank = i + 1;
    r.community_rank = i + 1;
    if (r.country) {
      const n = (byCountry.get(r.country) ?? 0) + 1;
      byCountry.set(r.country, n);
      r.country_rank = n;
    } else r.country_rank = 0;
    if (r.city) {
      const n = (byCity.get(r.city) ?? 0) + 1;
      byCity.set(r.city, n);
      r.city_rank = n;
    } else r.city_rank = 0;
  });
  return sorted;
}

function assignTitlesBadges(r: UserPopularityRecord): UserPopularityRecord {
  const titles: UserTitle[] = [];
  const badges: UserBadge[] = [];
  if (r.signals.messages_sent + r.signals.surveys_answered >= 5) titles.push("rookie_fan");
  if (r.signals.predictions_correct >= 20) titles.push("mvp_predictor");
  if (r.signals.messages_sent >= 50) titles.push("community_star");
  if (r.signals.referrals >= 10) titles.push("sponsor_champion");
  if (r.popularity_score >= 1000) titles.push("world_cup_legend");
  if (r.signals.predictions_correct >= 50) titles.push("oracle_master");
  if (r.signals.likes_received >= 100) titles.push("tactical_analyst");
  if (r.global_rank > 0 && r.global_rank <= 10) badges.push("top_10");
  if (r.signals.daily_streak >= 30) badges.push("streak_30");
  if (r.signals.predictions_correct >= 25) badges.push("prediction_expert");
  if (r.signals.shares >= 20) badges.push("brand_ambassador");
  if (r.signals.messages_sent >= 10) badges.push("verified_fan");
  r.titles = titles;
  r.badges = badges;
  return r;
}

export function ensurePr24Seed(): UserPopularityRecord[] {
  const cur = load();
  if (cur.length > 0) return cur;
  const seedRaw: Array<Partial<UserPopularityRecord> & { handle: string; signals: PopularitySignals }> = [
    { handle: "@carlos_mx", city: "CDMX", country: "MX", team: "MEX", signals: { messages_sent: 142, likes_received: 380, surveys_answered: 24, predictions_correct: 58, active_minutes: 920, shares: 33, referrals: 14, redemptions: 5, daily_streak: 32 } },
    { handle: "@luna_arg", city: "Buenos Aires", country: "AR", team: "ARG", signals: { messages_sent: 88, likes_received: 250, surveys_answered: 18, predictions_correct: 41, active_minutes: 740, shares: 22, referrals: 9, redemptions: 4, daily_streak: 21 } },
    { handle: "@miami_pro", city: "Miami", country: "US", team: "USA", signals: { messages_sent: 56, likes_received: 140, surveys_answered: 12, predictions_correct: 27, active_minutes: 510, shares: 14, referrals: 6, redemptions: 2, daily_streak: 14 } },
    { handle: "@madrid_fan", city: "Madrid", country: "ES", team: "ESP", signals: { messages_sent: 33, likes_received: 95, surveys_answered: 9, predictions_correct: 18, active_minutes: 380, shares: 8, referrals: 3, redemptions: 1, daily_streak: 8 } },
    { handle: "@bogota_10", city: "Bogotá", country: "CO", team: "COL", signals: { messages_sent: 21, likes_received: 60, surveys_answered: 6, predictions_correct: 11, active_minutes: 220, shares: 5, referrals: 2, redemptions: 1, daily_streak: 5 } },
  ];
  const rows: UserPopularityRecord[] = seedRaw.map((u, i) => {
    const popularity_score = Math.round(computeScore(u.signals));
    return {
      user_id: `seed-${i + 1}`,
      handle: u.handle,
      city: u.city,
      country: u.country,
      team: u.team,
      signals: u.signals,
      popularity_score,
      community_rank: 0,
      global_rank: 0,
      city_rank: 0,
      country_rank: 0,
      titles: [],
      badges: [],
      referral_code: genCode(),
      updated_at: Date.now(),
    };
  });
  const ranked = rerank(rows).map(assignTitlesBadges);
  save(ranked);
  return ranked;
}

export function listPopularity(): UserPopularityRecord[] {
  const rows = load();
  return rows.length ? rows : ensurePr24Seed();
}

export function bumpUserSignal(
  user_id: string,
  handle: string,
  patch: Partial<PopularitySignals>,
  meta?: { city?: string; country?: string; team?: string },
): UserPopularityRecord {
  const rows = load();
  let row = rows.find((r) => r.user_id === user_id);
  if (!row) {
    row = {
      user_id,
      handle,
      city: meta?.city,
      country: meta?.country,
      team: meta?.team,
      signals: { messages_sent: 0, likes_received: 0, surveys_answered: 0, predictions_correct: 0, active_minutes: 0, shares: 0, referrals: 0, redemptions: 0, daily_streak: 0 },
      popularity_score: 0,
      community_rank: 0,
      global_rank: 0,
      city_rank: 0,
      country_rank: 0,
      titles: [],
      badges: [],
      referral_code: genCode(),
      updated_at: Date.now(),
    };
    rows.push(row);
  }
  Object.keys(patch).forEach((k) => {
    const key = k as keyof PopularitySignals;
    row!.signals[key] = (row!.signals[key] || 0) + (patch[key] || 0);
  });
  row.popularity_score = Math.round(computeScore(row.signals));
  row.updated_at = Date.now();
  const ranked = rerank(rows).map(assignTitlesBadges);
  save(ranked);
  const updated = ranked.find((r) => r.user_id === user_id)!;
  void trackEvent("popularity_score_updated", { user_id, score: updated.popularity_score, patch });
  void trackEvent("leaderboard_updated", { scope: "global", top: ranked[0]?.handle });
  return updated;
}

export function getLeaderboard(
  scope: LeaderboardScope,
  category: LeaderboardCategory,
  filter?: { country?: string; city?: string; team?: string },
  limit = 20,
): UserPopularityRecord[] {
  let rows = listPopularity();
  if (scope === "country" && filter?.country) rows = rows.filter((r) => r.country === filter.country);
  if (scope === "city" && filter?.city) rows = rows.filter((r) => r.city === filter.city);
  if (scope === "team" && filter?.team) rows = rows.filter((r) => r.team === filter.team);
  const SORTS: Record<LeaderboardCategory, (r: UserPopularityRecord) => number> = {
    top_fans: (r) => r.signals.messages_sent + r.signals.likes_received,
    top_analysts: (r) => r.signals.surveys_answered + r.signals.predictions_correct,
    top_predictors: (r) => r.signals.predictions_correct,
    top_influencers: (r) => r.signals.shares + r.signals.likes_received,
    top_referrers: (r) => r.signals.referrals,
    top_consumers: (r) => r.signals.redemptions,
    top_winners: (r) => r.popularity_score,
  };
  const sortKey = SORTS[category];
  return [...rows].sort((a, b) => sortKey(b) - sortKey(a)).slice(0, limit);
}

export function buildReferralLink(code: string): string {
  const base = typeof window !== "undefined" ? window.location.origin : "https://versesport.com";
  return `${base}/?ref=${code}`;
}

export function registerReferral(referral_code: string, new_user: string): boolean {
  const rows = load();
  const ref = rows.find((r) => r.referral_code === referral_code);
  if (!ref) return false;
  ref.signals.referrals += 1;
  ref.popularity_score = Math.round(computeScore(ref.signals));
  save(rerank(rows).map(assignTitlesBadges));
  void trackEvent("referral_registered", { referral_code, new_user });
  return true;
}

export function shareUserContent(
  user_id: string,
  kind: "ranking" | "badge" | "prediction" | "reward",
  payload?: Record<string, unknown>,
) {
  const rows = load();
  const r = rows.find((x) => x.user_id === user_id);
  if (r) {
    r.signals.shares += 1;
    r.popularity_score = Math.round(computeScore(r.signals));
    save(rerank(rows).map(assignTitlesBadges));
  }
  void trackEvent("viral_share_completed", { user_id, kind, ...(payload || {}) });
}

export function extendStreak(user_id: string, days: number) {
  const rows = load();
  const r = rows.find((x) => x.user_id === user_id);
  if (!r) return;
  r.signals.daily_streak = days;
  r.popularity_score = Math.round(computeScore(r.signals));
  save(rerank(rows).map(assignTitlesBadges));
  void trackEvent("streak_extended", { user_id, days });
}

export function awardBadge(user_id: string, badge: UserBadge) {
  const rows = load();
  const r = rows.find((x) => x.user_id === user_id);
  if (!r) return;
  if (!r.badges.includes(badge)) r.badges.push(badge);
  save(rows);
  void trackEvent("badge_awarded", { user_id, badge });
}

// --- Heatmap + trending topics ---
export interface HeatCell {
  key: string;
  label: string;
  users: number;
  total_score: number;
}

export function getCommunityHeatmap(kind: "city" | "country" | "team"): HeatCell[] {
  const rows = listPopularity();
  const map = new Map<string, HeatCell>();
  rows.forEach((r) => {
    const key = (r[kind] as string | undefined) || "—";
    const cur = map.get(key) ?? { key, label: key, users: 0, total_score: 0 };
    cur.users += 1;
    cur.total_score += r.popularity_score;
    map.set(key, cur);
  });
  return [...map.values()].sort((a, b) => b.total_score - a.total_score);
}

export interface TrendingTopic {
  topic: string;
  category: "topic" | "player" | "brand";
  mentions: number;
}

const TRENDING_KEY = "vs:pr24:trending";

export function getTrendingTopics(): TrendingTopic[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TRENDING_KEY);
    if (raw) return JSON.parse(raw) as TrendingTopic[];
  } catch { /* noop */ }
  const seed: TrendingTopic[] = [
    { topic: "#MEXvsARG", category: "topic", mentions: 1240 },
    { topic: "#WorldCup2026", category: "topic", mentions: 980 },
    { topic: "Messi", category: "player", mentions: 612 },
    { topic: "Hirving Lozano", category: "player", mentions: 488 },
    { topic: "Adidas", category: "brand", mentions: 322 },
    { topic: "Coca-Cola", category: "brand", mentions: 254 },
  ];
  window.localStorage.setItem(TRENDING_KEY, JSON.stringify(seed));
  void trackEvent("trending_topics_updated", { count: seed.length });
  return seed;
}

export function bumpTrending(topic: string, category: TrendingTopic["category"]) {
  const list = getTrendingTopics();
  const cur = list.find((t) => t.topic === topic);
  if (cur) cur.mentions += 1;
  else list.push({ topic, category, mentions: 1 });
  if (typeof window !== "undefined")
    window.localStorage.setItem(TRENDING_KEY, JSON.stringify(list));
  void trackEvent("trending_topics_updated", { topic });
}

// --- Accordion persistence ---
const ACCORDION_KEY = "vs:pr24:cc:accordion";

export function loadAccordionState(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ACCORDION_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function persistAccordionState(state: Record<string, boolean>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ACCORDION_KEY, JSON.stringify(state));
}

export function toggleSection(id: string, open: boolean) {
  const s = loadAccordionState();
  s[id] = open;
  persistAccordionState(s);
  void trackEvent("command_center_section_toggled", { id, open });
}